import React from "react"
import * as Yup from "yup"
import {
  Box, Text, VStack, HStack, IconButton,
} from "@chakra-ui/react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import TextField from "components/ReactHookForm/TextField"
import SwitchField from "components/ReactHookForm/SwitchField"
import UrlField from "components/ReactHookForm/UrlField"
import { useFieldArray } from "react-hook-form"
import { useCurrentEvent } from "queries/events"
import { asDateWithTZ, URL_VALIDATOR_REGEX } from "utilities/strings"
import { Website } from "sharedTypes"
import * as api from "api/websites"
import { updateWebsite } from "context/actions"
import { useQueryClient } from "react-query"
import Icon from "components/elements/Icon"
import Link from "components/elements/Link"
import Button from "components/Buttons/Button"
import { normalizeBools } from "utilities/objects"
import { SidebarFormValues } from "pages/EventShow/WebPage/types"

interface Props {
  eventId: string
  website: Website
  onClose: () => void
  onHide: () => void
  onChange?: (values: SidebarFormValues) => void
}

const Sidebar = ({
  eventId, website, onClose, onHide, onChange,
}: Props) => {
  const queryClient = useQueryClient()
  const {
    data: currentEvent,
  } = useCurrentEvent()

  const onSubmit = (values: SidebarFormValues) => api.putWebsite(eventId, normalizeBools(values))
    .then(({ data }) => {
      updateWebsite(queryClient, eventId, data)
      onHide()
    })

  const validationSchema = Yup.object().shape({
    dateEnabled: Yup.boolean(),
    dateHeader: Yup.string(),
    dateLine1: Yup.string(),
    dateLine2: Yup.string(),
    locationEnabled: Yup.boolean(),
    locationVenue: Yup.string(),
    locationLine1: Yup.string(),
    locationLine2: Yup.string(),
    locationLine3: Yup.string(),
    linksEnabled: Yup.boolean(),
    links: Yup.array().of(
      Yup.string()
        .test("url", "Please enter a valid URL", (value) => {
          if (!value || value.trim() === "") return true // Allow empty strings

          return URL_VALIDATOR_REGEX.test(value)
        })
        .nullable(),
    ),
  })

  return (
    <ModalForm
      title="Sidebar"
      size="lg"
      onClose={onClose}
      initialValues={{
        dateEnabled: website.dateEnabled,
        dateHeader: website.dateHeader || "",
        dateLine1: website.dateLine1 || "",
        dateLine2: website.dateLine2 || "",
        locationEnabled: website.locationEnabled,
        locationVenue: website.locationVenue || "",
        locationLine1: website.locationLine1 || "",
        locationLine2: website.locationLine2 || "",
        locationLine3: website.locationLine3 || "",
        linksEnabled: website.linksEnabled,
        links: website.links && website.links.length > 0 ? website.links : [""],
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      disableSubmitWhenNotDirty
      onChange={onChange}
    >
      {({ control, watch, setValue }) => {
        const dateEnabled = watch("dateEnabled")
        const locationEnabled = watch("locationEnabled")
        const linksEnabled = watch("linksEnabled")

        const setDateDefaults = () => {
          if (currentEvent?.startDate && currentEvent?.timezone) {
            setValue("dateHeader", asDateWithTZ(currentEvent.startDate, currentEvent.timezone, "EEEE, MMMM d, yyyy"))
            setValue("dateLine1", asDateWithTZ(currentEvent.startDate, currentEvent.timezone, "h:mm a"))
            setValue("dateLine2", "")
          }
        }

        const setLocationDefaults = () => {
          if (currentEvent) {
            setValue("locationVenue", currentEvent.venue || "")
            setValue("locationLine1", currentEvent.addressLine1 || "")
            setValue("locationLine2", currentEvent.addressLine2 || "")
            setValue("locationLine3", `${currentEvent.city || ""}, ${currentEvent.state || ""} ${currentEvent.zipcode || ""}`.trim().replace(/^,\s*/, "").replace(/,\s*$/, ""))
          }
        }

        return (
          <VStack spacing={6} align="stretch">
            <Text fontSize="md">
              Show additional event details alongside the main content.
            </Text>
            <Box>
              <SwitchField
                name="dateEnabled"
                control={control}
                label="Date"
                mb={4}
                w="100%"
                fontSize="2xl"
                fontWeight="normal"
              />
              <VStack spacing={0} align="stretch" opacity={dateEnabled ? 1 : 0.5}>
                <TextField
                  name="dateHeader"
                  control={control}
                  label="Header"
                  variant="outline"
                  disabled={!dateEnabled}
                />
                <TextField
                  name="dateLine1"
                  control={control}
                  label="Line 1"
                  variant="outline"
                  disabled={!dateEnabled}
                />
                <TextField
                  name="dateLine2"
                  control={control}
                  label="Line 2"
                  variant="outline"
                  disabled={!dateEnabled}
                />
                <Box textAlign="right">
                  <Link
                    onClick={setDateDefaults}
                    color="blue.400"
                    fontSize="md"
                    isDisabled={!dateEnabled}
                    pointerEvents={!dateEnabled ? "none" : "auto"}
                  >
                    Reset to Event Defaults
                  </Link>
                </Box>
              </VStack>
            </Box>
            <Box>
              <SwitchField
                name="locationEnabled"
                control={control}
                label="Location"
                mb={4}
                w="100%"
                fontSize="2xl"
                fontWeight="normal"
              />
              <VStack spacing={0} align="stretch" opacity={locationEnabled ? 1 : 0.5}>
                <TextField
                  name="locationVenue"
                  control={control}
                  label="Venue"
                  placeholder="Venue"
                  variant="outline"
                  disabled={!locationEnabled}
                />
                <TextField
                  name="locationLine1"
                  control={control}
                  label="Line 1"
                  variant="outline"
                  disabled={!locationEnabled}
                />
                <TextField
                  name="locationLine2"
                  control={control}
                  label="Line 2"
                  variant="outline"
                  disabled={!locationEnabled}
                />
                <TextField
                  name="locationLine3"
                  control={control}
                  label="Line 3"
                  variant="outline"
                  disabled={!locationEnabled}
                />
                <Box textAlign="right">
                  <Link
                    onClick={setLocationDefaults}
                    color="blue.400"
                    fontSize="md"
                    isDisabled={!locationEnabled}
                    pointerEvents={!locationEnabled ? "none" : "auto"}
                  >
                    Reset to Event Defaults
                  </Link>
                </Box>
              </VStack>
            </Box>
            <Box>
              <SwitchField
                name="linksEnabled"
                control={control}
                label="Links"
                mb={4}
                w="100%"
                fontSize="2xl"
                fontWeight="normal"
              />
              <LinksList name="links" control={control} enabled={linksEnabled} />
            </Box>
          </VStack>
        )
      }}
    </ModalForm>
  )
}

const LinksList = ({ name, control, enabled }) => {
  const {
    fields, append, remove,
  } = useFieldArray<any, any, any>({ name, control })

  return (
    <VStack spacing={3} align="stretch" opacity={enabled ? 1 : 0.5}>
      {fields.map((link, index) => (
        <HStack key={link.id} spacing={2}>
          <Box flex={1}>
            <UrlField
              name={`links.${index}`}
              control={control}
              disabled={!enabled}
              variant="outline"
              label="URL"
            />
          </Box>
          <IconButton
            aria-label="Remove link"
            icon={<Icon icon="cross" size={5} />}
            onClick={() => remove(index)}
            isDisabled={!enabled}
            colorScheme="red"
            variant="ghost"
            size="sm"
          />
        </HStack>
      ))}
      <HStack spacing={2} align="flex-start">
        <Box flex={1}>
          <Box display="flex" alignItems="flex-start" flexDirection="row">
            {fields.length > 0 && <Box w={1 / 4} />}
            <Button
              onClick={() => append("")}
              isDisabled={!enabled}
              size="sm"
              variant="outline"
              leftIcon="plus"
            >
              Link
            </Button>
          </Box>
        </Box>
        {fields.length > 0 && <Box w={20} />}
      </HStack>
    </VStack>
  )
}

export default Sidebar
